import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature, FeatureAnalytics } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import sounds from 'src/lib/sounds';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { ButtonView, ImageScaleView } from '@play-co/timestep-core/ui';
import { Match2RewardItem } from 'src/game/minigame/match2-timestep/components/Match2RewardItem';
import gsap from 'gsap';
import { getRandomFloat } from '@play-co/components';
import { RadialShine } from 'src/game/components/shared/RadialShine';
import {
  playSpineAnimation,
  playSpineAnimationSequence,
} from 'src/lib/ui/utils/spine';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import StateObserver, { user } from 'src/StateObserver';
import { getJankenDailyRewards } from 'src/replicant/janken/janken.getters';
import type { JankenOutcome } from 'src/replicant/janken/janken.ruleset';

type CloseHandler = (ok: boolean) => void;

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

const assets = {
  radial: {
    loadMethod: spineLoader,
    url: 'assets/ui/janken/animation/spine.radial',
  },
  confetti: {
    loadMethod: spineLoader,
    url: 'assets/ui/janken/animation/spine.confetti',
  },
  medal: 'assets/ui/janken/icons/medal.png',
  rockIcon: 'assets/ui/janken/icons/rock.png',
};

// ---------------------------------------------------------------------------
// Outcome images — duplicated from PopupJanken to avoid coupling
// ---------------------------------------------------------------------------

const OUTCOME_IMAGES: Record<
  JankenOutcome,
  Record<'en' | 'ja', { image: string; width: number; height: number }>
> = {
  win: {
    en: {
      image: 'assets/ui/janken/results/en/win.png',
      width: 345,
      height: 180,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/win.png',
      width: 462,
      height: 218,
    },
  },
  lose: {
    en: {
      image: 'assets/ui/janken/results/en/lose.png',
      width: 675 * 0.9,
      height: 185 * 0.9,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/lose.png',
      width: 461,
      height: 218,
    },
  },
  draw: {
    en: {
      image: 'assets/ui/janken/results/en/draw.png',
      width: 450,
      height: 185,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/draw.png',
      width: 510,
      height: 218,
    },
  },
};

// ---------------------------------------------------------------------------
// Animation timing — fire-and-forget, no Spine awaits
// ---------------------------------------------------------------------------

const REWARD_TIMING = {
  initialDelay: 100,
  confettiDelay: 200,
  rewardRevealDelay: 300,
  ctaDelay: 800,
  instructionDelay: 1000,
  tapToContinueDelay: 1200,
};

// ---------------------------------------------------------------------------
// Popup
// ---------------------------------------------------------------------------

export default class PopupJankenReward extends PopupBasicLazy {
  private close: CloseHandler;
  private tracking!: FeatureAnalytics;
  private reward!: Match2Reward;
  private outcome!: JankenOutcome;
  private item!: Match2RewardItem;
  private ctaButton!: ButtonScaleViewWithText;
  private canTap = false;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.WIN_REWARD,
      },
      zIndex: 10000,
    });

    this.close = opts.close;
    this.buttonClose.hide();

    this.bg.updateOpts({
      backgroundColor: 'rgba(71, 24, 67, 0.85)',
    });

    // Ensure box renders above bg overlay
    this.box.updateOpts({ zIndex: 1 });

    // Full-screen tap to dismiss (same as PopupJankenInfo)
    new ButtonView({
      superview: this.box,
      width,
      height,
      zIndex: 100,
      onClick: () => {
        if (!this.canTap) return;
        this.ctaButton?.setDisabled(true);
        this.ctaButton?.updateOpts({ opacity: 0 });
        this.claim(false);
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      assets.radial,
      assets.confetti,
      assets.medal,
      assets.rockIcon,
    ]);
  }

  public async init(
    args: {
      reward: Match2Reward;
      outcome: JankenOutcome;
    } & FeatureAnalytics,
  ) {
    super.init({});
    this.reward = args.reward;
    this.outcome = args.outcome;
    this.canTap = false;
    this.tracking = {
      feature: args.feature,
      subFeature: args.subFeature,
    } as FeatureAnalytics;
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  // ---------------------------------------------------------------------------
  // Animation sequence — all fire-and-forget with fixed delays
  // ---------------------------------------------------------------------------

  private async animateIn() {
    await waitForItPromise(REWARD_TIMING.initialDelay);
    sounds.playSound('tada', 0.5);
    sounds.playSound('applause', 0.5);

    const centerX = this.box.style.width / 2;
    const centerY = this.box.style.height / 2 - 50;

    // 1. Radial burst (fire-and-forget)
    const radialSpine = new SpineView({
      superview: this.box,
      spinePath: assets.radial.url,
      x: centerX,
      y: centerY,
      scale: DOWNSCALE_FACTOR,
    });
    playSpineAnimationSequence(radialSpine, {
      sequence: ['radial_start', 'radial_loop'],
      loopLast: true,
    });

    // 2. Confetti (fire-and-forget after delay)
    waitForItPromise(REWARD_TIMING.confettiDelay).then(() => {
      const confettiSpine = new SpineView({
        superview: this.box,
        spinePath: assets.confetti.url,
        x: centerX,
        y: centerY,
        scale: DOWNSCALE_FACTOR,
      });
      playSpineAnimation(confettiSpine, { animation: 'confetti' });
    });

    // 3. Outcome image ("You Win!") + Reward icon
    waitForItPromise(REWARD_TIMING.rewardRevealDelay).then(() => {
      this.showOutcomeImage();
      this.showRewardItem(centerX, centerY);
    });

    // 4. CTA button ("Challenge Back!")
    waitForItPromise(REWARD_TIMING.ctaDelay).then(() => {
      this.showCtaButton();
    });

    // 6. Instruction bar (medal + "N Wins")
    waitForItPromise(REWARD_TIMING.instructionDelay).then(() => {
      this.showInstructionBar();
    });

    // 7. "Tap to Continue" + enable tap
    waitForItPromise(REWARD_TIMING.tapToContinueDelay).then(() => {
      this.showTapToContinue();
      this.canTap = true;
    });
  }

  // ---------------------------------------------------------------------------
  // UI elements
  // ---------------------------------------------------------------------------

  private showOutcomeImage() {
    const locale = StateObserver.getLocale() === 'ja' ? 'ja' : 'en';
    const { image, width, height } = OUTCOME_IMAGES[this.outcome][locale];
    const scaledWidth = width * 0.91;
    const scaledHeight = height * 0.91;
    const boxW = this.box.style.width;

    const outcomeImage = new ImageScaleView({
      superview: this.box,
      image,
      width: scaledWidth,
      height: scaledHeight,
      x: (boxW - scaledWidth) / 2,
      y: 220,
      centerAnchor: true,
      scale: 0.3,
      opacity: 0,
      zIndex: 20,
      canHandleEvents: false,
    });

    gsap.to(outcomeImage.style, {
      opacity: 1,
      scale: 1,
      duration: 0.4,
      ease: 'back.out(1.7)',
    });
  }

  private showRewardItem(centerX: number, centerY: number) {
    this.item = new Match2RewardItem({
      superview: this.box,
      size: 150,
      prefix: '',
      labelSize: 40,
      centerOnOrigin: true,
      opacity: 0,
      x: centerX,
      y: centerY,
      zIndex: 10,
    });
    this.item.setReward(this.reward);

    new RadialShine({
      superview: this.item,
      x: this.item.style.width / 2,
      y: this.item.style.height / 2,
      zIndex: -1,
      centerOnOrigin: true,
      opacity: 0.3,
    });

    this.item.updateOpts({ scale: 0 });
    gsap.to(this.item.style, { opacity: 1, duration: 0.2 });
    gsap.to(this.item.style, {
      scale: 1,
      duration: 0.4,
      ease: 'back.out',
    });

    // Gentle floating after pop-in
    gsap.to(this.item.style, {
      y: centerY + 10,
      duration: getRandomFloat(0.8, 1.2, Math.random),
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: 0.5,
    });

    this.item.addListener('ViewRemoved', () => {
      gsap.killTweensOf(this.item.style);
    });
  }

  private showCtaButton() {
    const isJa = StateObserver.getLocale() === 'ja';
    // Shift label right to make room for icon
    const btnWidth = isJa ? 360 : 500;
    this.ctaButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5 + 250,
      width: btnWidth,
      height: 104,
      centerOnOrigin: true,
      fontSize: 44,
      labelOffsetY: -1,
      font: 'Body',
      scale: 0,
      zIndex: 200,
      onClick: async () => {
        if (!this.canTap) return;
        this.ctaButton.setDisabled(true);
        this.ctaButton.updateOpts({ opacity: 0 });
        await this.claim(true);
      },
      localeText: () => i18n('janken.challengeBack'),
      tracking: {
        feature: this.tracking.feature,
        $subFeature: this.tracking.subFeature as string,
      },
    });

    // Rock icon inside button
    const iconX = 10;
    const iconSize = 80;
    new ImageScaleView({
      superview: this.ctaButton,
      image: assets.rockIcon,
      width: iconSize,
      height: iconSize,
      x: iconX,
      y: 12,
    });

    const labelX = iconX + iconSize + 8;
    const labelWidth = Math.round((btnWidth - labelX - 20) * 0.95);
    this.ctaButton.label.updateOpts({
      width: labelWidth,
    });
    this.ctaButton.setLabelPosition({ x: labelX, y: 4 });

    gsap.to(this.ctaButton.style, {
      scale: 1,
      duration: 0.4,
      ease: 'back.out',
    });
  }

  private showInstructionBar() {
    const boxW = this.box.style.width;
    const barW = 724;
    const barH = 89;
    const barY = this.box.style.height * 0.5 + 340;

    const bar = new View({
      superview: this.box,
      x: (boxW - barW) / 2,
      y: barY,
      width: barW,
      height: barH,
      opacity: 0,
      zIndex: 10,
    });

    // Background (separate view so opacity doesn't affect icon/text)
    new View({
      superview: bar,
      width: barW,
      height: barH,
      backgroundColor: '#000000',
      opacity: 0.42,
    });

    // Center icon + text as a group
    const isJa = StateObserver.getLocale() === 'ja';
    const iconW = downscale(69);
    const iconH = downscale(81);
    const padding = 20;
    const textW = isJa ? 300 : 500;
    const groupW = iconW + padding + textW;
    const groupX = (barW - groupW) / 2;

    // Medal icon
    new ImageScaleView({
      superview: bar,
      image: assets.medal,
      width: iconW,
      height: iconH,
      x: groupX,
      y: (barH - iconH) / 2,
      scaleMethod: 'contain',
    });

    // Win count text
    const dailyRewards = getJankenDailyRewards(user());
    new LangBitmapFontTextView({
      superview: bar,
      x: groupX + iconW + padding,
      y: (barH - 35) / 2,
      width: textW,
      height: 35,
      align: 'center',
      verticalAlign: 'center',
      size: 30,
      color: '#FFFFFF',
      font: bitmapFonts('Title'),
      canHandleEvents: false,
      localeText: () => i18n('janken.matchesWonToday', { n: dailyRewards + 1 }),
    });

    gsap.to(bar.style, {
      opacity: 1,
      duration: 0.35,
      ease: 'power2.out',
    });
  }

  private showTapToContinue() {
    const boxW = this.box.style.width;
    const text = new LangBitmapFontTextView({
      superview: this.box,
      width: 526,
      height: 53,
      x: (boxW - 526) / 2,
      y: this.box.style.height - 180,
      align: 'center',
      verticalAlign: 'center',
      size: 43,
      color: '#FFC700',
      font: bitmapFonts('Title'),
      canHandleEvents: false,
      localeText: () => i18n('janken.tapToContinue'),
      zIndex: 10,
    });

    text.updateOpts({ scale: 0, centerAnchor: true });
    gsap.to(text.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }

  // ---------------------------------------------------------------------------
  // Claim
  // ---------------------------------------------------------------------------

  private async claim(challengeBack: boolean) {
    this.canTap = false;
    sounds.playSound('ok');
    await this.item.grantReward(this.box);
    this.close(challengeBack);
  }
}
